import React, { useState } from 'react';
import { MapPin, Phone, Clock, Mail, MessageCircle, Send } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    inquiry_type: 'General Inquiry'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitMessage('Thank you! We will contact you soon via WhatsApp or phone.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        inquiry_type: 'General Inquiry'
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitMessage('Something went wrong. Please try WhatsApp or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = 'Hello, I have a question about Petsmart Puppies.';
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div data-testid="contact-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
      <SEO />
      {/* Hero Section */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(to bottom right, #E0F2FE, #FFFFFF)',
          borderBottom: '1px solid var(--neutral-border)'
        }}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            Contact Us
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
            Get in touch with us for any questions or to visit our store
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-main)' }}>
                Get In Touch
              </h2>

              <div className="space-y-6">
                <div
                  className="flex items-start gap-4 p-4 rounded-2xl"
                  style={{ backgroundColor: 'white' }}
                >
                  <div
                    className="flex-shrink-0 p-3 rounded-full"
                    style={{ backgroundColor: 'var(--primary-light)' }}
                  >
                    <MapPin size={24} style={{ color: 'var(--primary-main)' }} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1" style={{ color: 'var(--text-main)' }}>
                      Visit Our Store
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      Shop No:- D1 Ground Floor<br />
                      Near DR Bhimrao Ambedkar Multispeciality Hospital<br />
                      Indergarhi, Sector 31, Noida, UP 201303
                    </p>
                    <a
                      href="https://maps.app.goo.gl/XswStGTLKycyKPZt7"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="get-directions-link"
                      className="inline-block mt-2 font-semibold"
                      style={{ color: 'var(--primary-main)' }}
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div
                  className="flex items-start gap-4 p-4 rounded-2xl"
                  style={{ backgroundColor: 'white' }}
                >
                  <div
                    className="flex-shrink-0 p-3 rounded-full"
                    style={{ backgroundColor: 'var(--accent-light)' }}
                  >
                    <Phone size={24} style={{ color: 'var(--accent-main)' }} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1" style={{ color: 'var(--text-main)' }}>
                      Call Us
                    </h3>
                    <a
                      href="tel:+917217648679"
                      data-testid="call-link"
                      style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}
                      className="hover:underline"
                    >
                      +91 7217648679
                    </a>
                  </div>
                </div>

                <div
                  className="flex items-start gap-4 p-4 rounded-2xl"
                  style={{ backgroundColor: 'white' }}
                >
                  <div
                    className="flex-shrink-0 p-3 rounded-full"
                    style={{ backgroundColor: 'var(--secondary-light)' }}
                  >
                    <Clock size={24} style={{ color: 'var(--secondary-main)' }} />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1" style={{ color: 'var(--text-main)' }}>
                      Store Hours
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                      Open Daily<br />
                      Closes at 10:00 PM
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleWhatsApp}
                  data-testid="whatsapp-contact-button"
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold transition-all hover:-translate-y-1"
                  style={{
                    backgroundColor: '#25D366',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 10px 30px rgba(37, 211, 102, 0.3)'
                  }}
                >
                  <MessageCircle size={24} />
                  Chat on WhatsApp
                </button>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="shadow-soft"
              style={{
                backgroundColor: 'white',
                padding: '2.5rem',
                borderRadius: '1.5rem'
              }}
            >
              <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-main)' }}>
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block mb-2 font-semibold"
                      style={{ color: 'var(--text-main)', fontSize: '0.875rem' }}
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="name-input"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--neutral-border)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block mb-2 font-semibold"
                      style={{ color: 'var(--text-main)', fontSize: '0.875rem' }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="email-input"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--neutral-border)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block mb-2 font-semibold"
                      style={{ color: 'var(--text-main)', fontSize: '0.875rem' }}
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="phone-input"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--neutral-border)',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="inquiry_type"
                      className="block mb-2 font-semibold"
                      style={{ color: 'var(--text-main)', fontSize: '0.875rem' }}
                    >
                      Inquiry Type
                    </label>
                    <select
                      id="inquiry_type"
                      name="inquiry_type"
                      value={formData.inquiry_type}
                      onChange={handleChange}
                      data-testid="inquiry-type-select"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--neutral-border)',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option>General Inquiry</option>
                      <option>Puppy Reservation</option>
                      <option>Product Information</option>
                      <option>Pet Care Advice</option>
                      <option>Store Visit</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block mb-2 font-semibold"
                      style={{ color: 'var(--text-main)', fontSize: '0.875rem' }}
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      data-testid="message-textarea"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--neutral-border)',
                        fontSize: '0.875rem',
                        resize: 'vertical'
                      }}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-form-button"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-bold transition-all hover:-translate-y-1"
                    style={{
                      backgroundColor: 'var(--primary-main)',
                      color: 'white',
                      border: 'none',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>

                  {submitMessage && (
                    <div
                      className="p-4 rounded-lg text-center"
                      style={{
                        backgroundColor: 'var(--accent-light)',
                        color: 'var(--accent-main)',
                        fontWeight: '600'
                      }}
                    >
                      {submitMessage}
                    </div>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Actions */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-main)' }}>
            How to Reach Us
          </h2>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://maps.app.goo.gl/XswStGTLKycyKPZt7"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: 'var(--primary-main)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                display: 'inline-block'
              }}
            >
              📍 Get Directions
            </a>
            <a
              href="tel:+917217648679"
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: 'var(--accent-main)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                display: 'inline-block'
              }}
            >
              📞 Call Now
            </a>
            <button
              onClick={handleWhatsApp}
              className="transition-all hover:-translate-y-1"
              style={{
                backgroundColor: '#25D366',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              💬 Chat on WhatsApp
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;